import * as THREE from 'three'
import Fading from 'utils/fading'

import isMobile from 'utils/isMobile'

import { DEFAULT_MOBILE_TEXTURE_SIZE } from 'Renderer/constants'
import {
    loadManifestTexture,
} from 'Renderer/manifestImageLoader'

const fadingTime = 0.25 // seconds
const maxWallsCount = 16

class CompositeBackground {
    constructor(renderer, intrinsics, width, height, manifest, panoTexture) {
        this.renderer = renderer
        this.fullscreenQuad = null
        this.material = null
        this.colorFading = []
        this.fadingStartColor = null
        this.fadingTargetColor = null

        this.createFullscreenQuad(intrinsics, width, height, manifest, panoTexture)
    }

    static intrinsicsRectGeo(intrinsics, width, height, d = 1) {
        const fx = intrinsics[0]
        const fy = intrinsics[1]
        const px = intrinsics[2]
        const py = intrinsics[3]

        const intrinsicRect = new THREE.PlaneGeometry(
            (width / fx) * d, (-height / fy) * d)
        intrinsicRect.translate(
            d * 0.5 * ((width - px) - px) / fx,
            d * -0.5 * ((height - py) - py) / fy, -1.0 * d)

        return intrinsicRect
    }
    
    createFullscreenQuad(intrinsics, width, height, manifest, panoTexture) {
        const rangeArray = [...Array(maxWallsCount).keys()]

        const defaultUniforms = {
            shadingMap: { value: null },
            residualMap: { value: null },
            segmentationMap: { value: null },
            panoMap: { value: null },

            customColors: { value: rangeArray.map(() => new THREE.Color(0xffffff)) },
            prevColors: { value: rangeArray.map(() => new THREE.Color(0xffffff)) },

            colorsAssigned: { value: rangeArray.map(() => 0.0) },
            colorLerp: { value: rangeArray.map(() => 0.0) },
            colorsHasPrev: { value: rangeArray.map(() => 0.0) },
        }
        const defines = {
            MAX_WALLS_COUNT: maxWallsCount,
        }

        if (!manifest.segmentation_0) {
            defines.USE_OLD_SEGMENTATION = ''
        }

        this.material = new THREE.ShaderMaterial({
            uniforms: defaultUniforms,
            defines,
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                uniform sampler2D shadingMap;
                uniform sampler2D panoMap;
                uniform sampler2D residualMap;
                uniform sampler2D segmentationMap;

                uniform vec4 customColors[MAX_WALLS_COUNT];
                uniform vec4 prevColors[MAX_WALLS_COUNT];

                uniform float colorLerp[MAX_WALLS_COUNT];
                uniform float colorsHasPrev[MAX_WALLS_COUNT];
                uniform float colorsAssigned[MAX_WALLS_COUNT];

                varying vec2 vUv;

                // Dynamic indices are not supported in webgl1, i.e. Safari
                vec4 getCustomColor(int index) {
                    #pragma unroll_loop_start
                    for (int i = 0; i < MAX_WALLS_COUNT; i++) {
                        if (i == index) {
                            return customColors[i];
                        }
                    }
                    #pragma unroll_loop_end
                    return vec4(0.0);
                }

                vec4 getPrevColor(int index) {
                    #pragma unroll_loop_start
                    for (int i = 0; i < MAX_WALLS_COUNT; i++) {
                        if (i == index) {
                            return prevColors[i];
                        }
                    }
                    #pragma unroll_loop_end
                    return vec4(0.0);
                }

                float getColorAssigned(int index) {
                    #pragma unroll_loop_start
                    for (int i = 0; i < MAX_WALLS_COUNT; i++) {
                        if (i == index) {
                            return colorsAssigned[i];
                        }
                    }
                    #pragma unroll_loop_end
                    return 0.0;
                }

                float getColorLerp(int index) {
                    #pragma unroll_loop_start
                    for (int i = 0; i < MAX_WALLS_COUNT; i++) {
                        if (i == index) {
                            return colorLerp[i];
                        }
                    }
                    #pragma unroll_loop_end
                    return 0.0;
                }

                float getColorHasPrev(int index) {
                    #pragma unroll_loop_start
                    for (int i = 0; i < MAX_WALLS_COUNT; i++) {
                        if (i == index) {
                            return colorsHasPrev[i];
                        }
                    }
                    #pragma unroll_loop_end
                    return 0.0;
                }

                void main() {
                    vec4 shading = texture2D(shadingMap, vUv);
                    vec4 residual = texture2D(residualMap, vUv);
                    vec4 segmentation = texture2D(segmentationMap, vUv);
                    vec4 panoColor = texture2D(panoMap, vUv);

                    vec4 wallColor = vec4(0.0);
                    vec4 finalColor = vec4(0.0);
                    #ifdef USE_OLD_SEGMENTATION
                        float segmentationValue = segmentation.b;
                    #else
                        float segmentationValue = segmentation.g;
                    #endif
                    int planeIdx = int((segmentationValue * 255.0) + 0.5);
                    float lerp = getColorLerp(planeIdx);
                    if (getColorAssigned(planeIdx) > 0.0) {
                        vec4 customColor = getCustomColor(planeIdx);
                        if (lerp >= 1.0) {
                            wallColor = customColor;
                            finalColor = wallColor * shading + residual;
                        } else if (getColorHasPrev(planeIdx) > 0.0) {
                            wallColor = mix(getPrevColor(planeIdx), customColor, lerp);
                            finalColor = wallColor * shading + residual;
                        } else {
                            wallColor = customColor;
                            finalColor = wallColor * shading + residual;
                            finalColor = mix(panoColor, finalColor, lerp);
                        }
                    } else if (getColorHasPrev(planeIdx) > 0.0) {
                        vec4 prevFinalColor = getPrevColor(planeIdx) * shading + residual;
                        finalColor = mix(prevFinalColor, panoColor, lerp);
                    } else {
                        finalColor = panoColor;
                    }

                    gl_FragColor = finalColor;
                }
            `,
            transparent: false,
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide,
        })

        const rect = CompositeBackground.intrinsicsRectGeo(intrinsics, width, height)
        this.fullscreenQuad = new THREE.Mesh(rect, this.material)

        const onMapLoad = (texture, uniformName) => {
            // eslint-disable-next-line no-param-reassign
            texture.flipY = false
            // eslint-disable-next-line no-param-reassign
            texture.encoding = THREE.sRGBEncoding
            // eslint-disable-next-line no-param-reassign
            texture.needsUpdate = true

            let resultTex = texture
            const resizeTexture = isMobile()
            if (resizeTexture) {
                const newWidth = (Math.round(0.5 * (texture.image && texture.image.width))) || DEFAULT_MOBILE_TEXTURE_SIZE
                const newHeight = (Math.round(0.5 * (texture.image && texture.image.height))) || DEFAULT_MOBILE_TEXTURE_SIZE
                const resized = this.renderer.textureHelper.getResizedCopy(texture, newWidth, newHeight)
                if (resized) {
                    resultTex = resized
                    texture.dispose()
                }
            }
            this.material.uniforms[uniformName].value = resultTex
            this.material.needsUpdate = true
        }

        Promise.all([
            loadManifestTexture(manifest, 'shading_0', this.renderer.defaultDownloadOptions),
            loadManifestTexture(manifest, 'residual_0', this.renderer.defaultDownloadOptions),
            loadManifestTexture(manifest, 'normals_segmentation_0', this.renderer.defaultDownloadOptions)
        ])
        .then(([
            { texture: shadingMap },
            { texture: residualMap},
            { texture: segmentationMap}
        ]) => {
            onMapLoad(shadingMap, 'shadingMap')
            onMapLoad(residualMap, 'residualMap')
            onMapLoad(segmentationMap, 'segmentationMap')
        })

        this.material.uniforms.panoMap.value = panoTexture
        this.fullscreenQuad.name = 'background image'
    }

    startInterpolation(planeIdx, targetColor) {
        if (planeIdx < 0 || planeIdx >= maxWallsCount) {
            return
        }

        if (this.colorFading[planeIdx]) {
            this.colorFading[planeIdx].stop()
        }

        // indices in the segmenation are 1 based
        const unifromIdx = planeIdx + 1

        if (this.material.uniforms.colorsAssigned.value[unifromIdx] > 0.0) {
            this.material.uniforms.prevColors.value[unifromIdx]
                .copy(this.material.uniforms.customColors.value[unifromIdx])
            this.material.uniforms.colorsHasPrev.value[unifromIdx] = 1.0
        } else {
            this.material.uniforms.colorsHasPrev.value[unifromIdx] = 0.0
        }

        if (targetColor) {
            this.material.uniforms.customColors.value[unifromIdx].copy(targetColor)
            this.material.uniforms.colorsAssigned.value[unifromIdx] = 1.0
        } else {
            this.material.uniforms.colorsAssigned.value[unifromIdx] = 0.0
        }
        this.material.uniforms.colorLerp.value[unifromIdx] = 0.0

        this.colorFading[planeIdx] = new Fading(fadingTime, (t) => {
            this.material.uniforms.colorLerp.value[unifromIdx] = t
            if (this.renderer) {
                this.renderer.setRenderIsDirty()
            }
        }, () => {
            this.colorFading[planeIdx] = null
        })

        if (this.renderer) {
            this.renderer.setRenderIsDirty()
        }
    }

    setPlaneColor(planeIdx, color, interpolate = false) {
        if (planeIdx < 0 || planeIdx >= maxWallsCount) {
            return
        }
        if (!color) {
            this.unsetPlaneColor(planeIdx, interpolate)
            return
        }
        const targetColor = new THREE.Color()
        if ((typeof color === 'string' || color instanceof String)) {
            targetColor.set(color)
        } else if (Array.isArray(color)) {
            const r = color[0] > 1.0 ? color[0] / 255.0 : color[0]
            const g = color[1] > 1.0 ? color[1] / 255.0 : color[1]
            const b = color[2] > 1.0 ? color[2] / 255.0 : color[2]
            targetColor.setRGB(r, g, b)
        } else {
            targetColor.copy(color)
        }

        if (interpolate) {
            this.startInterpolation(planeIdx, targetColor)
        } else {
            // indices in the segmenation are 1 based
            this.material.uniforms.colorsAssigned.value[planeIdx + 1] = 1.0
            this.material.uniforms.customColors.value[planeIdx + 1].copy(targetColor)
            this.material.uniforms.colorLerp.value[planeIdx + 1] = 1.0
            if (this.renderer) {
                this.renderer.setRenderIsDirty()
            }
        }
    }

    unsetPlaneColor(planeIdx, interpolate) {
        if (planeIdx < 0 || planeIdx >= maxWallsCount) {
            return
        }
        if (interpolate) {
            this.startInterpolation(planeIdx, null)
        } else {
            this.material.uniforms.customColors.value[planeIdx].set(0xffffff);
            this.material.uniforms.colorsAssigned.value[planeIdx] = 0.0;
            if (this.renderer) {
                this.renderer.setRenderIsDirty()
            }
        }
    }
}

export default CompositeBackground